import { DownOutlined, IdcardOutlined, LogoutOutlined } from "@ant-design/icons";
import { Dropdown } from "antd";
import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "../i18n";
import { RouteNames } from "../routes";

/** Two initials from a display name, falling back to the first of an email. */
export function initialsOf(name?: string | null, email?: string | null): string {
    const source = (name ?? "").trim();
    if (source.length > 0) {
        const parts = source.split(/\s+/).slice(0, 2);
        return parts.map(part => part[0]!).join("").toUpperCase();
    }
    return (email ?? "?").trim().charAt(0).toUpperCase() || "?";
}

/**
 * Identity and sign-out, reachable from every screen.
 *
 * Signing out used to mean navigating to the Account page first, which is a
 * page's worth of chrome for one button. The page still exists — this is just
 * the door to it that is always in reach.
 */
export const UserMenu = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const t = useTranslate();
    const [open, setOpen] = useState(false);

    const name = auth.user?.profile?.name ?? auth.user?.profile?.preferred_username ?? "";
    const email = auth.user?.profile?.email ?? "";
    const initials = initialsOf(name, email);

    const signOut = async () => {
        setOpen(false);
        localStorage.removeItem("token");
        await auth.signoutRedirect({
            post_logout_redirect_uri: window.location.origin,
            id_token_hint: auth.user?.id_token
        });
        await auth.removeUser();
    };

    const overlay = (
        <div className="user-menu">
            <div className="user-menu__head">
                <span className="avatar">{initials}</span>
                <span style={{ minWidth: 0 }}>
                    <div className="user-menu__name">{name || t("account.title")}</div>
                    {email.length > 0 ? <div className="user-menu__sub">{email}</div> : null}
                </span>
            </div>
            <button
                type="button"
                className="user-menu__item"
                onClick={() => {
                    setOpen(false);
                    navigate(`/${RouteNames.accounts}`);
                }}
            >
                <IdcardOutlined />
                {t("account.title")}
            </button>
            <button
                type="button"
                className="user-menu__item user-menu__item--danger"
                onClick={signOut}
            >
                <LogoutOutlined />
                {t("common.signOut")}
            </button>
        </div>
    );

    return (
        <Dropdown
            overlay={overlay}
            trigger={["click"]}
            placement="bottomRight"
            visible={open}
            onVisibleChange={setOpen}
        >
            <button type="button" className="user-chip" aria-label={t("account.title")}>
                <span className="avatar">{initials}</span>
                <span className="user-chip__name">{name || t("account.title")}</span>
                <DownOutlined className="user-chip__caret" />
            </button>
        </Dropdown>
    );
};
